import * as React from 'react'
import { E, O, pipe, T, TE } from '@packages/prelude'
import * as RD from '@devexperts/remote-data-ts'
import * as domain from '@packages/domain'

type PreviewResponse = domain.Api.IAGenPreviewResponse.Type

export interface UsePreviewPollingArgs {
  getStatus: (token: string) => TE.TaskEither<domain.Error.Type, O.Option<PreviewResponse>>
  intervalMs?: number
}

export interface UsePreviewPollingResult {
  previewData: RD.RemoteData<domain.Error.Type, PreviewResponse>
  start: (token: string) => void
  set: (rd: RD.RemoteData<domain.Error.Type, PreviewResponse>) => void
  reset: () => void
}

export const usePreviewPolling = ({ getStatus, intervalMs = 3000 }: UsePreviewPollingArgs): UsePreviewPollingResult => {
  const [previewData, setPreviewData] = React.useState<RD.RemoteData<domain.Error.Type, PreviewResponse>>(RD.initial)
  const timer = React.useRef<ReturnType<typeof setTimeout>>()
  const mounted = React.useRef(true)

  // Hold the latest poll function in a ref to avoid stale closures in the setTimeout callback
  // while keeping react-hooks/exhaustive-deps clean (the ref is always current).
  const pollRef = React.useRef<(token: string) => void>(() => undefined)

  // Mark unmounted and clear any pending timer so an in-flight status continuation cannot
  // schedule a new poll or update state after the component is gone.
  React.useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
      clearTimeout(timer.current)
    }
  }, [])

  // One status request, then step the machine: Left → failure; Right(None) → re-poll after the
  // interval; Right(Some) → success.
  const poll = React.useCallback(
    (token: string): void => {
      void pipe(
        getStatus(token),
        T.map(
          E.fold(
            err => {
              if (mounted.current) setPreviewData(RD.failure(err))
            },
            O.fold(
              () => {
                if (mounted.current) timer.current = setTimeout(() => pollRef.current(token), intervalMs)
              },
              resp => {
                if (mounted.current) setPreviewData(RD.success(resp))
              }
            )
          )
        )
      )()
    },
    [getStatus, intervalMs]
  )

  // Keep the ref in sync with the latest poll function so the timeout always calls the
  // current version without poll depending on itself.
  React.useEffect(() => {
    pollRef.current = poll
  }, [poll])

  // Cancel any pending timer before starting a new poll chain so two rapid start() calls
  // cannot run concurrent poll chains.
  const start = React.useCallback(
    (token: string) => {
      clearTimeout(timer.current)
      setPreviewData(RD.pending)
      poll(token)
    },
    [poll]
  )
  // The useState setter is already a stable reference, so it is exposed directly.
  const set = setPreviewData
  const reset = React.useCallback(() => {
    clearTimeout(timer.current)
    setPreviewData(RD.initial)
  }, [])

  return { previewData, start, set, reset }
}
